/**
 * Query-string codecs. A query value travels as a string; these schemas accept the typed value
 * on the client side (their *input* type) and the string on the server side, so that
 * `api.call(c, { query: { limit: 50, archived: true, ids: ['a', 'b'] } })` is typed and the
 * server parses `?limit=50&archived=true&ids=a,b`.
 *
 * Client serialisation (`serializeQuery`): strings, numbers and booleans as text, arrays joined
 * with commas, plain objects as JSON, `null`/`undefined` left out.
 */
import { z } from 'zod';
import { currentMessageLanguage, valueErrorMessage } from '@dataroom/core';

/** Integer from `?n=12`; the client passes a number. */
function int() {
  return z.coerce.number<number>().int();
}

/** Number from `?n=1.5`; the client passes a number. */
function number() {
  return z.coerce.number<number>();
}

/** Boolean from `?flag=true|false|1|0`; the client passes a boolean. */
function bool() {
  return z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1');
}

/** List from `?ids=a,b,c` (or repeated keys); the client passes an array. */
function list<T extends z.ZodType>(item: T, options: { max?: number } = {}) {
  const max = options.max ?? 500;
  return z
    .union([z.array(z.string()), z.string()])
    .transform((v) => (Array.isArray(v) ? v : v === '' ? [] : v.split(',')))
    .pipe(z.array(item as unknown as z.ZodType<unknown, string>).max(max)) as unknown as z.ZodType<
    z.output<T>[],
    z.input<T>[]
  >;
}

/** JSON value from `?filter={…}`; the client passes the object. */
function json<T extends z.ZodType>(schema: T) {
  return z
    .union([
      z.string().transform((s, ctx) => {
        try {
          return JSON.parse(s) as unknown;
        } catch {
          // Typed (`invalid_json`) and in the language of the request, like every value error.
          ctx.addIssue({
            code: 'custom',
            message: valueErrorMessage('invalid_json', {}, currentMessageLanguage()),
            params: { code: 'invalid_json' },
          });
          return z.NEVER;
        }
      }),
      z.record(z.string(), z.unknown()),
      z.array(z.unknown()),
    ])
    .pipe(schema) as unknown as z.ZodType<z.output<T>, z.input<T>>;
}

/** Query codecs for contract `query` schemas. */
export const q = { int, number, bool, list, json };

export type QueryValue = string | number | boolean | null | undefined | readonly unknown[] | object;

/** Serialise typed query values into a query string (`?a=1&b=x,y`), or `''` when empty. */
export function serializeQuery(query: Record<string, QueryValue> | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) params.set(k, v.map(String).join(','));
    else if (typeof v === 'object') params.set(k, JSON.stringify(v));
    else params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}
